import { useEffect, useRef, useState } from 'react';

import { PROJECT_VARIANTS } from '../../constants';
import { fetchCache, mediaCache, setInCache, setInCacheWithMetadata } from '../../service/mediaCache';
import sendRequest from '../../service/sendRequest';
import callApi from '../../service/callApi';
import getFileNameFromHeaders from '../../utils/getFileNameFromHeaders';
import getMediaTypeByFilename from '../../utils/getMediaTypeByFilename';

const useAIStudioMedia = ({ id, projectVariant = PROJECT_VARIANTS.ADMIN, onLoadMedia }) => {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const controllerRef = useRef(null);

  const handleError = () => {
    setMediaUrl(null);
    setHasError(true);
    setIsLoading(false);
  };

  const handleSuccess = (response) => {
    const blobUrl = URL.createObjectURL(new Blob([response.data]));
    const fileName = getFileNameFromHeaders(response.headers);
    const mediaType = getMediaTypeByFilename(fileName);
    setInCacheWithMetadata(mediaCache, id, blobUrl, { mediaType, fileName });
    setMediaUrl(blobUrl);
    setMediaType(mediaType);
    setFileName(fileName);
    onLoadMedia?.(blobUrl);
  };

  const handleLoadMedia = async () => {
    setIsLoading(true);

    const cachedPromise = fetchCache.get(id);

    const fetchPromise =
      cachedPromise ||
      sendRequest(
        () => {
          const controller = new AbortController();
          controllerRef.current = controller;

          if (projectVariant === PROJECT_VARIANTS.ADMIN) {
            return callApi.get(`/api/studio/favorite/download?id=${id}`, {
              responseType: 'arraybuffer',
              signal: controller.signal,
            });
          }
        },
        {
          onError: () => {
            fetchCache.delete(id);
            handleError();
          },
        },
      );
    setInCache(fetchCache, id, fetchPromise);

    const response = await fetchPromise;
    handleSuccess(response);

    setIsLoading(false);
  };

  useEffect(() => {
    if (!id) return;

    if (mediaCache.has(id)) {
      setMediaUrl(mediaCache.get(id).value);
      setMediaType(mediaCache.get(id).mediaType);
      setFileName(mediaCache.get(id).fileName);
      onLoadMedia?.(mediaCache.get(id).value);
      return;
    }

    handleLoadMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, projectVariant]);

  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  return {
    mediaUrl,
    mediaType,
    fileName,
    error: hasError,
    isLoading,
    setError: setHasError,
  };
};

export default useAIStudioMedia;
